import React, { useEffect, useMemo, useRef, useState } from "react";
import { workerTerm } from "./utils/workerTerm";
import LiveView from "./pages/LiveView";
import DashboardPage from "./pages/DashboardPage";
import PlaybackPage from "./pages/PlaybackPage";
import SettingsPage from "./pages/SettingsPage";
import DevicesPage from "./pages/DevicesPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminApprovalPage from "./pages/admin/AdminApprovalPage";
import AdminCamerasPage from "./pages/admin/AdminCamerasPage";
import AdminRetentionPage from "./pages/admin/AdminRetentionPage";
import AdminSystemPage from "./pages/admin/AdminSystemPage";

import ForcePasswordChange from "./components/auth/ForcePasswordChange";
import EventsPage from "./pages/EventsPage";
import SafetyReportPage from "./pages/SafetyReportPage";
import WorkSchedulePage from "./pages/WorkSchedulePage";
import NoticeSenderPage from "./pages/NoticeSenderPage";
import MessagesPage from "./pages/MessagesPage";
import MapPage from "./pages/MapPage";
import ComingSoon from "./components/ComingSoon";

import NotificationBell from "./components/alerts/NotificationBell";
import AlertToast from "./components/alerts/AlertToast";
import SystemHealthPanel from "./components/admin/SystemHealthPanel";
import AlertPanel from "./components/alerts/AlertPanel";
import DangerDismissModal from "./components/alerts/DangerDismissModal";
import PpeAckModal from "./components/alerts/PpeAckModal";
import MenuIcon from "./components/layout/MenuIcon";

import useAlertCenter from "./hooks/useAlertCenter";
import useCameraStatus from "./hooks/useCameraStatus";
import { refreshCameraStatuses } from "./utils/cameraStatusStore";
import {
  getPermissionFlags,
  hasPermission,
  hasRawPermission,
  isSuperAdminUser,
} from "./utils/permissions";
import { menuItems } from "./utils/menuConfig";
import { canSeeOwnerConsole, OWNER_CONSOLE_ENABLED } from "./utils/ownerConsole";

/*
  운영사(민트블루) 전용 콘솔 — 지연 로딩으로 «별도 청크»에 둔다.
  일반 import 로 두면 메뉴를 꺼도 코드가 메인 번들에 실려,
  납품본 JS 를 열면 «구독 관리·만료일» 같은 글자가 그대로 보인다.
*/
const OwnerSubscriptionPage = React.lazy(() =>
  import("./pages/owner/SubscriptionPage")
);

// 가입 폼 팀 항목에서 "고르지 않음"(빈 값)과 "팀이 없는 사람"을 구분하는 값.
// 둘을 같은 빈 값으로 두면 작업자가 그냥 지나쳐도 가입돼 TeamName 이 NULL 로 쌓인다.
// 서버에는 빈 문자열로 바꿔 보내므로 이 값이 DB 에 저장되는 일은 없다.
const TEAM_NONE = "__none__";

const PAGE_TITLES = {
  dashboard: "대시보드",
  live: "실시간 관제",
  playback: "다시보기",
  events: "이벤트 내역",
  "safety-report": "안전 리포트",
  "work-schedule": "작업 배정",
  notice: "공지 발송",
  messages: "메시지",
  devices: "장비",
  workers: "작업자",
  map: "지도",
  logs: "로그",
  settings: "설정",
  "admin-users": "관리자 > 사용자 관리",
  "admin-approval": "관리자 > 승인 관리",
  "admin-cameras": "관리자 > 카메라 관리",
  "admin-retention": "관리자 > 데이터 보관 정책",
  "owner-subscriptions": "운영 > 구독 관리",
  "admin-system": "관리자 > 시스템 설정",
};

const PAGE_PATHS = {
  dashboard: "/",
  live: "/live",
  playback: "/playback",
  events: "/events",
  "safety-report": "/safety-report",
  "work-schedule": "/work-schedule",
  notice: "/notice",
  messages: "/messages",
  devices: "/devices",
  workers: "/workers",
  map: "/map",
  logs: "/logs",
  settings: "/settings",
  "admin-users": "/admin/users",
  "admin-approval": "/admin/approval",
  "admin-cameras": "/admin/cameras",
  "admin-retention": "/admin/retention",
  "owner-subscriptions": "/owner/subscriptions",
  "admin-system": "/admin/system",
};

const PATH_TO_PAGE = Object.entries(PAGE_PATHS).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

function getPageFromLocation() {
  if (typeof window === "undefined") return "dashboard";

  let path = window.location.pathname || "/";

  // /playback/ 처럼 뒤에 슬래시가 붙어도 /playback 으로 인식
  if (path.length > 1 && path.endsWith("/")) {
    path = path.slice(0, -1);
  }

  return PATH_TO_PAGE[path] || "dashboard";
}

function LicenseRestrictedPanel({ licenseStatus, onNavigate }) {
  const plan = licenseStatus?.license?.plan || "-";

  return (
    <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 text-amber-900 shadow-sm dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
      <div className="text-sm font-semibold uppercase tracking-[0.18em] text-amber-600 dark:text-amber-300">
        LICENSE RESTRICTED
      </div>
      <h2 className="mt-2 text-2xl font-bold">다시보기 사용 불가</h2>
      <p className="mt-3 text-sm leading-6">
        현재 적용된 라이선스 요금제에서는 다시보기 기능을 사용할 수 없습니다.
      </p>
      <div className="mt-4 rounded-2xl bg-white/70 p-4 text-sm dark:bg-black/20">
        현재 요금제: <span className="font-bold">{plan}</span>
      </div>
      <button
        type="button"
        onClick={() => onNavigate("live")}
        className="mt-5 rounded-2xl bg-amber-600 px-4 py-3 text-sm font-semibold text-white hover:bg-amber-700"
      >
        실시간 관제로 이동
      </button>
    </div>
  );
}

export default function App() {
  const [page, setPage] = useState(() => getPageFromLocation());
  const [me, setMe] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  const [licenseStatus, setLicenseStatus] = useState(null);
  const [licenseLoading, setLicenseLoading] = useState(false);

  const [loginId, setLoginId] = useState("");
  const [loginPw, setLoginPw] = useState("");
  const [loginMsg, setLoginMsg] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [signupId, setSignupId] = useState("");
  const [signupPw, setSignupPw] = useState("");
  const [signupPwConfirm, setSignupPwConfirm] = useState("");
  const [signupCompanyName, setSignupCompanyName] = useState("");
  const [signupName, setSignupName] = useState("");
  const [signupPhoneLast, setSignupPhoneLast] = useState("");
  const [signupMsg, setSignupMsg] = useState("");
  const [branches, setBranches] = useState([]); // 입력한 회사에 등록된 지점 목록
  const [signupBranchName, setSignupBranchName] = useState(""); // 선택한 지점(회사에 지점 있을 때만)
  const [teams, setTeams] = useState([]); // 선택한 지점에 등록된 팀 목록
  const [signupTeamName, setSignupTeamName] = useState(""); // 선택한 팀(그 지점에 팀 있을 때만)
  const [signupVehicleNo, setSignupVehicleNo] = useState(""); // 차량번호(선택)
  /*
    차량번호 칸을 띄울지 — 회사마다 다르다.
    삼우는 «설치 차량» 단위로 작업을 배정해서 받지만, 다른 회사는 차량이 없거나 상관이 없다.
    쓰지도 않는 칸이 뜨면 필수인 줄 알고 아무 값이나 적는다.
  */
  const [signupVehicleAsk, setSignupVehicleAsk] = useState(false);
  const [signupIdChecked, setSignupIdChecked] = useState(false);
  const [signupCheckingId, setSignupCheckingId] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [dangerText, setDangerText] = useState("현재 위험 상태를 표시합니다.");
  const [dangerCount, setDangerCount] = useState(0);
  const [todayEventCount, setTodayEventCount] = useState(0);
  // 대시보드 '오늘 이벤트' — 서버(/api/today-event-count)가 오늘·계정범위로 정확히 센 값.
  // null이면 아직 미수신(클라이언트 폴백 사용).
  const [serverTodayCount, setServerTodayCount] = useState(null);
  const [activeDangerItem, setActiveDangerItem] = useState(null);

  // AI 안전장구(PPE) 미확인 위반 — 사람이 확인할 때까지 위험으로 남는다.
  const [activePpe, setActivePpe] = useState([]);
  // 목록(activePpe)은 100건까지만 받는다. 카드에 띄울 <전체 건수>는 서버가 따로 세어준다.
  const [activePpeTotal, setActivePpeTotal] = useState(0);
  const [ppeModalOpen, setPpeModalOpen] = useState(false);
  const [ppeAckSubmittingId, setPpeAckSubmittingId] = useState(null);

  /*
    onlineCount 는 공유 스토어(cameraStatusStore)에서 온다.
    예전에는 App.js 가 /api/my-cameras/status 를 setInterval 5초로 직접 불렀고,
    LivePreview·PriorityPreviewCard·DevicesPage 도 각자 같은 API를 불러
    한 탭에서 같은 요청이 네 번씩 나갔다. 이제 탭당 1회로 합쳐진다.
    실시간 조회 권한이 없으면 구독하지 않으므로 폴링 자체가 일어나지 않는다.
  */
  const { onlineCount } = useCameraStatus({
    enabled: !!me && hasPermission(me, "CanLiveView"),
  });

  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  const [cameras, setCameras] = useState([]);
  const [camerasLoading, setCamerasLoading] = useState(false);

  const [dangerModalOpen, setDangerModalOpen] = useState(false);
  const [workerName, setWorkerName] = useState("");
  const [dismissReason, setDismissReason] = useState("");
  const [dangerSubmitting, setDangerSubmitting] = useState(false);

  const historyReadyRef = useRef(false);
  const skipNextPushRef = useRef(false);

  // ── 히든 메뉴: 우측 상단 사용자 이름을 5번 연속 클릭하면 서버 점검 패널이 열린다.
  //    고객사 담당자에게 노출할 화면이 아니라 메뉴에 두지 않는다.
  //    다만 <숨기는 것은 방어가 아니므로> 실제 접근 통제는 서버에서 한다
  //    (routes/systemHealthRoutes.js — SUPER_ADMIN 이 아니면 데이터 자체를 주지 않는다).
  const [healthOpen, setHealthOpen] = useState(false);
  const nameClickRef = useRef({ count: 0, at: 0 });
  const handleNameClick = () => {
    const now = Date.now();
    const r = nameClickRef.current;
    // 1.5초 안에 연속으로 눌러야 인정 — 우연히 눌려서 열리는 것을 막는다
    r.count = now - r.at > 1500 ? 1 : r.count + 1;
    r.at = now;
    if (r.count >= 5) {
      r.count = 0;
      setHealthOpen(true);
    }
  };

  const APP_API_BASE = "";

  // 입력한 회사명으로 그 회사의 지점 목록 조회 (있으면 지점 선택 노출). 300ms 디바운스.
  useEffect(() => {
    const c = signupCompanyName.trim();
    setSignupBranchName("");
    if (!c) {
      setBranches([]);
      return;
    }
    const t = setTimeout(() => {
      fetch(`${APP_API_BASE}/api/branches?company=${encodeURIComponent(c)}`, {
        credentials: "include",
      })
        .then((r) => r.json())
        .then((d) => {
          setBranches(d?.success && Array.isArray(d.branches) ? d.branches : []);
        })
        .catch(() => setBranches([]));
    }, 300);
    return () => clearTimeout(t);
  }, [signupCompanyName]);

  // 선택한 지점에 등록된 팀 목록 조회 (있으면 팀 선택 노출).
  //   팀은 <회사 + 지점> 아래 단위라 지점이 정해져야 조회된다.
  //   팀을 등록하지 않은 회사·지점은 빈 목록이라 팀 칸이 뜨지 않는다.
  useEffect(() => {
    const c = signupCompanyName.trim();
    const b = signupBranchName.trim();
    setSignupTeamName("");

    if (!c || !b) {
      setTeams([]);
      return;
    }

    const t = setTimeout(() => {
      fetch(
        `${APP_API_BASE}/api/teams?company=${encodeURIComponent(
          c
        )}&branch=${encodeURIComponent(b)}`,
        { credentials: "include" }
      )
        .then((r) => r.json())
        .then((d) => {
          setTeams(d?.success && Array.isArray(d.teams) ? d.teams : []);
        })
        .catch(() => setTeams([]));
    }, 300);

    return () => clearTimeout(t);
  }, [signupCompanyName, signupBranchName]);

  // 회사명을 입력하면 그 회사가 차량번호를 쓰는지 서버에 물어본다.
  useEffect(() => {
    const c = signupCompanyName.trim();
    if (!c) {
      setSignupVehicleAsk(false);
      return undefined;
    }
    let dead = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `${APP_API_BASE}/api/signup/company-config?company=${encodeURIComponent(c)}`
        );
        const j = await res.json().catch(() => null);
        if (!dead) setSignupVehicleAsk(!!j?.vehicleEnabled);
      } catch {
        if (!dead) setSignupVehicleAsk(false);
      }
    }, 300); // 글자를 칠 때마다 부르지 않게 잠깐 기다린다
    return () => {
      dead = true;
      clearTimeout(t);
    };
  }, [signupCompanyName]);
  const SENSOR_API_BASE = APP_API_BASE;

  /*
    기능 ON/OFF 는 «서버 라이선스»와 «회사 구독» 두 단계다.
      · 라이선스 : 이 서버가 그 모듈을 팔 수 있는가 (서버 전체)
      · 구독     : 그 회사가 그 모듈을 쓰기로 했는가 (회사별)
    둘 다 켜져 있어야 화면에 나온다. 회사 쪽이 꺼져 있으면,
    바디캠만 쓰는 고객에게 민트워치·메시지가 보이지 않는다.
  */
  const [companyFeatures, setCompanyFeatures] = useState(null);

  useEffect(() => {
    if (!me) {
      setCompanyFeatures(null);
      return;
    }
    let dead = false;
    (async () => {
      try {
        const res = await fetch(`${APP_API_BASE}/api/my-subscription`, {
          credentials: "include",
          cache: "no-store",
        });
        const j = await res.json().catch(() => null);
        if (!dead) setCompanyFeatures(j?.success ? j.state : null);
      } catch {
        if (!dead) setCompanyFeatures(null);
      }
    })();
    return () => {
      dead = true;
    };
  }, [me]);

  // 라이선스 기능 옵션(유료 모듈) 플래그. 화면에서 PPE/민트워치 표시 여부에 사용.
  const licenseFeatures = useMemo(() => {
    const lic = licenseStatus?.license || {};
    // 회사 구독에서 끈 기능은 라이선스가 켜져 있어도 끈다.
    //   구독 정보가 없으면(null) 예전처럼 라이선스만 따른다 — 갑자기 기능이 사라지지 않게.
    const coPpe = companyFeatures?.ppeEnabled;
    const coWatch = companyFeatures?.mintwatchEnabled;
    return {
      ppeEnabled: lic.ppeEnabled === true && coPpe !== false,
      mintwatchEnabled: lic.mintwatchEnabled !== false && coWatch !== false,
    };
  }, [licenseStatus, companyFeatures]);

  // 권한 + 라이선스를 합친 실제 사용 권한.
  // 민트워치 미발급이면 관리자라도 워치/메시지 관련 기능을 전부 끈다
  // (메뉴·장비·대시보드·종 어디서든 민트워치 흔적이 사라진다).
  const permissionFlags = useMemo(() => {
    const f = getPermissionFlags(me);
    if (licenseFeatures.mintwatchEnabled === false) {
      return { ...f, canWatchDanger: false, canMessages: false };
    }
    return f;
  }, [me, licenseFeatures.mintwatchEnabled]);

  const playbackAllowed = useMemo(() => {
    if (!licenseStatus) return true;
    if (licenseStatus.ok === false) return false;
    return licenseStatus?.license?.playbackEnabled !== false;
  }, [licenseStatus]);

  const licenseBadge = useMemo(() => {
    if (licenseLoading && !licenseStatus) {
      return {
        label: "라이선스 확인 중",
        detail: "",
        danger: false,
      };
    }

    if (!licenseStatus) {
      return {
        label: "라이선스 미확인",
        detail: "",
        danger: true,
      };
    }

    if (!licenseStatus.ok) {
      return {
        label: "라이선스 오류",
        detail: licenseStatus.reason || "",
        danger: true,
      };
    }

    const license = licenseStatus.license || {};
    const plan = license.plan || "-";
    const maxDevices = Number(license.maxDevices || 0);
    const playbackText = license.playbackEnabled ? "다시보기 가능" : "다시보기 제한";

    return {
      label: plan,
      detail: `${maxDevices}대 · ${playbackText}`,
      danger: false,
    };
  }, [licenseLoading, licenseStatus]);

  const alertScopeReady = useMemo(() => {
    if (!me) return false;

    const role = String(me?.roleCode || "").trim().toUpperCase();
    const adminLike =
      role === "SUPER_ADMIN" ||
      role === "ADMIN" ||
      role === "HQ_MANAGER" ||
      role === "BRANCH_MANAGER";

    if (adminLike) return true;

    /*
     * 일반 USER는 자기 계정에 연결된 cameras 기준으로 이벤트를 필터한다.
     * cameras 로딩 중에 먼저 이벤트 로그를 불러오면
     * 대시보드 이벤트 카드가 잠깐 "최근 이벤트가 없습니다"로 보였다가
     * 다시 정상 이벤트로 바뀌는 깜빡임이 생긴다.
     */
    if (hasPermission(me, "CanLiveView") && camerasLoading) {
      return false;
    }

    return true;
  }, [me, camerasLoading]);

  const {
    alertOpen,
    setAlertOpen,
    unreadAlerts,
    toastItem,
    setToastItem,
    recentAlertLogs,
    activeDangerLogs,
    logs,
    todayEventCount: alertTodayCount,
    handleClearAlerts,
  } = useAlertCenter(SENSOR_API_BASE, {
    enabled:
      !!me &&
      alertScopeReady &&
      (permissionFlags.canAlertCenter || permissionFlags.canEventData),
    allowBell: permissionFlags.canAlertCenter,
    allowEventLogs: permissionFlags.canEventData,
    includeDangerSummary: permissionFlags.canWatchDanger,
    currentUser: me,
    scopedCameras: cameras,
  });

  const navigateToPage = (nextPage, options = {}) => {
    const resolvedPage = PAGE_TITLES[nextPage] ? nextPage : "dashboard";
    const replace = !!options.replace;

    setPage((prev) => {
      if (prev === resolvedPage && !replace) return prev;
      return resolvedPage;
    });

    if (typeof window !== "undefined") {
      const nextPath = PAGE_PATHS[resolvedPage] || "/";
      const currentPath = window.location.pathname || "/";
      const state = { page: resolvedPage };

      if (replace) {
        window.history.replaceState(state, "", nextPath);
      } else if (currentPath !== nextPath) {
        window.history.pushState(state, "", nextPath);
      } else {
        window.history.replaceState(state, "", nextPath);
      }
    }
  };

  const refreshLicenseStatus = async () => {
    try {
      setLicenseLoading(true);

      const res = await fetch(`${APP_API_BASE}/api/license/status`, {
        cache: "no-store",
        credentials: "include",
      });

      const data = await res.json().catch(() => null);

      if (data) {
        setLicenseStatus(data);
      } else {
        setLicenseStatus({
          ok: false,
          reason: "LICENSE_STATUS_EMPTY",
          message: "라이선스 상태 응답이 비어 있습니다.",
        });
      }

      return data;
    } catch (err) {
      const fallback = {
        ok: false,
        reason: "LICENSE_STATUS_FETCH_ERROR",
        message: "라이선스 상태를 불러오지 못했습니다.",
        error: err?.message || String(err),
      };

      setLicenseStatus(fallback);
      return fallback;
    } finally {
      setLicenseLoading(false);
    }
  };

  useEffect(() => {
    refreshLicenseStatus();
  }, []);

  useEffect(() => {
    if (dark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [dark]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const initialPage = getPageFromLocation();
    window.history.replaceState(
      { page: initialPage },
      "",
      PAGE_PATHS[initialPage] || "/"
    );
    historyReadyRef.current = true;

    const handlePopState = (event) => {
      const statePage = event.state?.page;
      const nextPage = PAGE_TITLES[statePage] ? statePage : getPageFromLocation();
      skipNextPushRef.current = true;
      setPage(nextPage);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (!historyReadyRef.current || typeof window === "undefined") return;
    if (skipNextPushRef.current) {
      skipNextPushRef.current = false;
      return;
    }

    const nextPath = PAGE_PATHS[page] || "/";
    const currentPath = window.location.pathname || "/";
    const currentStatePage = window.history.state?.page;

    if (currentPath !== nextPath) {
      window.history.pushState({ page }, "", nextPath);
      return;
    }

    if (currentStatePage !== page) {
      window.history.replaceState({ page }, "", nextPath);
    }
  }, [page]);

  const refreshMe = async () => {
    try {
      const res = await fetch(`${APP_API_BASE}/api/me`, {
        credentials: "include",
      });
      const data = await res.json();

      if (data?.loggedIn && data?.user) {
        // 회사별 용어(작업자→설치팀장) 판별용 회사명 저장
        try {
          localStorage.setItem(
            "companyName",
            data.user.CompanyName || data.user.companyName || ""
          );
        } catch (_) {}
        setMe(data.user);
        return data.user;
      }

      setMe(null);
      return null;
    } catch {
      setMe(null);
      return null;
    }
  };

  const refreshMyCameras = async () => {
    try {
      setCamerasLoading(true);

      const res = await fetch(`${APP_API_BASE}/api/my-cameras`, {
        credentials: "include",
      });

      const data = await res.json();

      if (res.ok && data?.success && Array.isArray(data.cameras)) {
        setCameras(data.cameras);
      } else {
        setCameras([]);
      }
    } catch {
      setCameras([]);
    } finally {
      setCamerasLoading(false);
    }
  };

  // 로그인 직후 등 "지금 바로 한 번" 이 필요한 곳에서 쓴다.
  // 주기 폴링은 공유 스토어가 맡는다(요청이 탭당 1회로 합쳐진다).
  const refreshMyCameraStatuses = () => refreshCameraStatuses();

  const pullDangerSummary = async () => {
    if (!permissionFlags.canWatchDanger) {
      setTodayEventCount(0);
      setDangerCount(0);
      setDangerText("현재 위험 상태를 표시합니다.");
      setActiveDangerItem(null);
      return;
    }

    try {
      const res = await fetch(`${APP_API_BASE}/api/sos-summary`, {
        cache: "no-store",
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();

      const todayCount = Number(
        data?.todayCount ?? data?.today ?? data?.countToday ?? 0
      );
      const activeCount = Number(
        data?.activeCount ?? data?.active ?? data?.countActive ?? 0
      );
      const activeEvents = Array.isArray(data?.activeEvents)
        ? data.activeEvents
        : Array.isArray(data?.items)
        ? data.items
        : [];

      setTodayEventCount(todayCount);
      setDangerCount(activeCount);

      if (activeEvents.length > 0) {
        const top = activeEvents[0];
        const who =
          top?.deviceName ||
          top?.label ||
          top?.workerName ||
          top?.userName ||
          top?.name ||
          "담당자 미상";
        const reason =
          top?.reason ||
          top?.message ||
          top?.note ||
          top?.state ||
          "사유 없음";

        setDangerText(`${who} / ${reason}`);
        setActiveDangerItem(top);
      } else {
        setDangerText("현재 위험 상태를 표시합니다.");
        setActiveDangerItem(null);
      }
    } catch {
      setTodayEventCount(0);
      setDangerCount(0);
      setDangerText("현재 위험 상태를 표시합니다.");
      setActiveDangerItem(null);
    }
  };

  const getDefaultPage = (user) => {
    if (!user) return "dashboard";
    if (hasPermission(user, "CanDashboard")) return "dashboard";
    if (hasPermission(user, "CanLiveView")) return "live";
    if (hasPermission(user, "CanPlayback") && playbackAllowed) return "playback";
    if (hasPermission(user, "CanMessages")) return "messages";
    if (hasPermission(user, "CanSettings")) return "settings";
    if (hasPermission(user, "CanEvents")) return "events";
    if (hasPermission(user, "CanDevices")) return "devices";
    if (hasPermission(user, "CanWorkers")) return "workers";
    if (hasPermission(user, "CanMap")) return "map";
    if (hasPermission(user, "CanLogs")) return "logs";
    return "dashboard";
  };

  const getTitle = () => {
    // '작업자' 페이지 타이틀은 회사 용어에 맞춤(삼우=설치팀장)
    if (page === "workers") return workerTerm();
    return PAGE_TITLES[page] || "MONITY";
  };

  useEffect(() => {
    refreshMe().finally(() => {
      setAuthChecked(true);
    });
  }, []);

  const isAdmin =
    me &&
    (me.roleCode === "SUPER_ADMIN" ||
      me.roleCode === "ADMIN" ||
      me.roleCode === "HQ_MANAGER" ||
      me.roleCode === "BRANCH_MANAGER");
  // 본사 전용(전역 설정 등) 노출용
  const isSuperAdmin = me && me.roleCode === "SUPER_ADMIN";

  const visibleMenus = menuItems.filter((item) => {
    if (item.key === "playback" && !playbackAllowed) return false;
    // 민트워치(메시지): ①라이선스 발급 + ②계정별 CanMessages 둘 다여야 노출.
    // 관리자여도 계정별 값을 그대로 따른다(관리자 권한 우회 예외) — 슈퍼어드민이 특정 계정의
    // 민트워치를 끄면 그 계정이 관리자여도 메시지 메뉴가 사라져야 하기 때문.
    // AI(PPE) 전용 메뉴 — 회사가 AI 를 안 쓰면 숨긴다(빈 화면만 뜨는 메뉴를 없앤다).
    if (item.ppe && !licenseFeatures.ppeEnabled) return false;
    if (item.mintwatch) {
      if (licenseFeatures.mintwatchEnabled === false) return false;
      if (isSuperAdminUser(me)) return true; // 슈퍼어드민(운영자)은 항상 노출
      return item.perm ? hasRawPermission(me, item.perm) : true;
    }
    if (!item.perm) return true;
    return hasPermission(me, item.perm);
  });

  useEffect(() => {
    if (!authChecked) return;
    if (!me) return;

    /*
     * /playback 직접 진입 시 대시보드로 튕기는 문제 방지.
     * 로그인 상태와 라이선스 상태가 완전히 확인되기 전에
     * 권한 체크가 먼저 실행되면 잘못된 페이지 이동이 발생할 수 있다.
     */
    if (!licenseStatus || licenseLoading) return;

    const blockedPages = {
      dashboard: "CanDashboard",
      live: "CanLiveView",
      playback: "CanPlayback",
      events: "CanEvents",
      messages: "CanMessages",
      devices: "CanDevices",
      workers: "CanWorkers",
      map: "CanMap",
      logs: "CanLogs",
      settings: "CanSettings",
    };

    /*
     * 다시보기가 라이선스에서 제한된 경우에는
     * dashboard로 튕기지 않고 App 안에서 LicenseRestrictedPanel을 보여준다.
     */
    if (page === "playback" && !playbackAllowed) {
      return;
    }

    if (page in blockedPages && !hasPermission(me, blockedPages[page])) {
      console.warn("[PAGE PERMISSION WARNING] 권한 부족으로 판단됐지만 자동 이동은 막음", {
        currentPage: page,
        requiredPermission: blockedPages[page],
        user: me,
        defaultPage: getDefaultPage(me),
        playbackAllowed,
        licenseStatus,
      });

      /*
       * 중요:
       * 여기서 navigateToPage(getDefaultPage(me))를 실행하면
       * 다시보기/이벤트/장비/지도 등 페이지 조작 중에도
       * 권한값이 순간적으로 false로 판단될 때 대시보드로 튕길 수 있다.
       *
       * 메뉴 노출은 visibleMenus에서 이미 권한 기준으로 제어하고 있으므로,
       * 일반 페이지는 여기서 강제 이동하지 않는다.
       */
      return;
    }

    if (
      (page === "admin-users" ||
        page === "admin-approval" ||
        page === "admin-cameras" ||
        page === "admin-retention" ||
        page === "admin-system") &&
      !isAdmin
    ) {
      navigateToPage(getDefaultPage(me), { replace: true });
    }

    // 운영사 전용(구독 관리) — 주소를 직접 쳐서 들어오는 것도 막는다.
    //   고객사 서버에서는 빌드 단계에서 아예 빠지고(REACT_APP_OWNER_CONSOLE=false),
    //   우리 서버에서도 SUPER_ADMIN 외에는 들어올 수 없다.
    if (page === "owner-subscriptions" && !canSeeOwnerConsole(me)) {
      navigateToPage(getDefaultPage(me), { replace: true });
    }

    // 회사 구독에서 끈 기능은 주소를 직접 쳐도 못 들어간다.
    if (page === "work-schedule" && companyFeatures?.workScheduleEnabled === false) {
      navigateToPage(getDefaultPage(me), { replace: true });
    }
    if (page === "notice" && companyFeatures?.noticeEnabled === false) {
      navigateToPage(getDefaultPage(me), { replace: true });
    }
    // 안전 리포트 — AI 를 안 쓰는 회사는 주소로도 못 들어간다.
    if (page === "safety-report" && !licenseFeatures.ppeEnabled) {
      navigateToPage(getDefaultPage(me), { replace: true });
    }
    // 구독 정보는 로그인 뒤에 따로 들어온다. 여기 넣지 않으면
    // «AI 꺼진 회사가 안전 리포트 주소로 들어와 있을 때» 다시 검사하지 않는다.
  }, [
    authChecked,
    page,
    me,
    isAdmin,
    playbackAllowed,
    licenseStatus,
    licenseLoading,
    licenseFeatures.ppeEnabled,
    companyFeatures?.workScheduleEnabled,
    companyFeatures?.noticeEnabled,
  ]);

  useEffect(() => {
    if (!me || !permissionFlags.canWatchDanger) {
      setTodayEventCount(0);
      setDangerCount(0);
      setDangerText("현재 위험 상태를 표시합니다.");
      setActiveDangerItem(null);
      return;
    }

    /*
     * 기존에는 대시보드/이벤트 화면 진입 시 한 번만 SOS 요약을 조회했다.
     * 그래서 화면을 켜둔 상태에서 SOS가 발생하면 종 알림과 이벤트 목록에는 보이지만,
     * 오늘 이벤트/위험 감지 카운터와 위험 해제 모달 기준 데이터는 갱신되지 않았다.
     *
     * 대시보드 또는 이벤트 화면에서는 3초마다 SOS 요약을 다시 조회해
     * 카운터와 위험 해제 모달 데이터를 동일하게 최신 상태로 맞춘다.
     */
    if (page !== "dashboard" && page !== "events") {
      return;
    }

    pullDangerSummary();

    const dangerSummaryInterval = setInterval(() => {
      pullDangerSummary();
    }, 3000);

    return () => {
      clearInterval(dangerSummaryInterval);
    };
  }, [me, permissionFlags.canWatchDanger, page]);

  /*
    카메라 상태 폴링은 cameraStatusStore 가 대신한다(위 useCameraStatus 구독).
    예전 코드는 setInterval 5초 고정이라 탭이 숨겨져도 계속 전수 조회했고,
    LivePreview·PriorityPreviewCard·DevicesPage 가 각자 또 같은 API를 불러
    한 탭에서 같은 요청이 네 번씩 나갔다.
  */

  // 대시보드 '오늘 이벤트' — 서버가 오늘(KST)·계정범위로 정확히 센 값을 주기 폴링.
  // 클라이언트 슬라이스/스코프 집계보다 우선. (관리자 계정 카운트 어긋남 해결)
  useEffect(() => {
    if (!me || !permissionFlags.canEventData || page !== "dashboard") {
      return;
    }
    let alive = true;
    const pull = async () => {
      try {
        const res = await fetch("/api/today-event-count", {
          cache: "no-store",
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json();
        if (alive && typeof data?.count === "number") {
          setServerTodayCount(data.count);
        }
      } catch {
        /* 무시 — 다음 주기에 재시도 */
      }
    };
    pull();
    const iv = setInterval(pull, 5000); // 위험감지(5초)와 정렬 — '오늘 이벤트' 지연 최소화
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [me, permissionFlags.canEventData, page]);

  useEffect(() => {
    // 이 목록은 아래 settingsGroupActive(메뉴 강조)와 반드시 같아야 한다.
    // 예전에 작업배정·공지가 여기만 빠져 있어서, 그 두 메뉴를 누르면
    // 강조는 "설정 그룹"인데 그룹이 접혀버렸다.
    if (
      page === "settings" ||
      page.startsWith("admin-") ||
      page === "work-schedule" ||
      page === "notice"
    ) {
      setSettingsOpen(true);
    } else {
      setSettingsOpen(false);
    }

    // 작업배정·공지 버튼은 사이드바에서 '관리자' 묶음 안에 들어 있다.
    // 여기서 admin- 페이지만 열어두면, 그 두 메뉴를 누르는 순간 자기가 들어 있던
    // 묶음이 닫혀 버튼들이 통째로 사라진다. 그래서 같이 열어둔다.
    if (
      page.startsWith("admin-") ||
      page === "work-schedule" ||
      page === "notice"
    ) {
      setAdminOpen(true);
    } else {
      setAdminOpen(false);
    }

    setMobileMenuOpen(false);
  }, [page]);

  useEffect(() => {
    if (me && hasPermission(me, "CanLiveView")) {
      refreshMyCameras();
    } else {
      setCameras([]);
      setCamerasLoading(false);
    }
  }, [me]);

  useEffect(() => {
    setSignupIdChecked(false);
  }, [signupId]);

  const handleLogout = async () => {
    try {
      await fetch(`${APP_API_BASE}/api/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {}

    setMe(null);
    try { localStorage.removeItem("companyName"); } catch (_) {}
    setCameras([]);
    setCamerasLoading(false);
    setAuthMode("login");
    setLoginMsg("");
    setSignupMsg("");
    window.location.href = "/";
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loggingIn) return;
    setLoginMsg("");
    setLoggingIn(true);

    // 서버 응답이 없을 때 무한 대기 방지(버튼이 "안 먹는" 것처럼 보이는 문제 방지)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 12000);

    try {
      const res = await fetch(`${APP_API_BASE}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id: loginId, pw: loginPw }),
        signal: controller.signal,
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setLoginMsg(data.message || "로그인에 실패했습니다.");
        return;
      }

      await refreshLicenseStatus();

      const freshUser = await refreshMe();

      /*
       * 중요:
       * 로그인 직후 기본 페이지 이동은 먼저 처리한다.
       * 기존처럼 카메라/상태/위험요약 로딩을 모두 기다린 뒤 이동하면,
       * 사용자가 첫 메뉴를 클릭한 뒤에 navigateToPage가 늦게 실행되어
       * 다시 대시보드로 튕기는 현상이 생긴다.
       */
      navigateToPage(getDefaultPage(freshUser), { replace: true });
      setSettingsOpen(false);
      setAdminOpen(false);

      /*
       * 로그인 후 부가 데이터는 페이지 이동을 막지 않도록 백그라운드로 불러온다.
       * 여기서 await 하지 않아야 첫 메뉴 이동이 덮어써지지 않는다.
       */
      if (freshUser && hasPermission(freshUser, "CanLiveView")) {
        refreshMyCameras();
        refreshMyCameraStatuses();
      }

      if (freshUser && hasPermission(freshUser, "CanWatchDanger")) {
        pullDangerSummary();
      }
    } catch (err) {
      if (err && err.name === "AbortError") {
        setLoginMsg("서버에 연결할 수 없습니다. 네트워크 상태를 확인해주세요.");
      } else {
        setLoginMsg("로그인 중 오류가 발생했습니다. 다시 시도해주세요.");
      }
    } finally {
      clearTimeout(timeoutId);
      setLoggingIn(false);
    }
  };

  const handleCheckSignupId = async () => {
    setSignupMsg("");
    const trimmedId = String(signupId || "").trim();

    if (!trimmedId) {
      setSignupMsg("아이디를 입력해주세요.");
      setSignupIdChecked(false);
      return;
    }

    if (trimmedId.length < 4) {
      setSignupMsg("아이디는 4자 이상으로 입력해주세요.");
      setSignupIdChecked(false);
      return;
    }

    try {
      setSignupCheckingId(true);

      const res = await fetch(`${APP_API_BASE}/api/signup/check-id`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: trimmedId }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setSignupIdChecked(false);
        setSignupMsg(data.message || "아이디 중복 확인에 실패했습니다.");
        return;
      }

      if (!data.available) {
        setSignupIdChecked(false);
        setSignupMsg(data.message || "이미 사용 중인 아이디입니다.");
        return;
      }

      setSignupIdChecked(true);
      setSignupMsg(data.message || "사용 가능한 아이디입니다.");
    } catch {
      setSignupIdChecked(false);
      setSignupMsg("아이디 중복 확인 중 오류가 발생했습니다.");
    } finally {
      setSignupCheckingId(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupMsg("");

    if (!signupIdChecked) {
      setSignupMsg("아이디 중복 확인을 먼저 진행해주세요.");
      return;
    }

    if (!signupCompanyName.trim()) {
      setSignupMsg("회사명을 입력해주세요.");
      return;
    }

    if (!signupName.trim()) {
      setSignupMsg("이름을 입력해주세요.");
      return;
    }

    if (!/^\d{4}$/.test(signupPhoneLast)) {
      setSignupMsg("휴대폰 끝 4자리를 정확히 입력해주세요.");
      return;
    }

    // 팀 칸이 뜬 경우에만 고르게 한다(팀을 등록한 회사·지점에서만 뜬다).
    // 팀이 없는 관리자는 "해당 없음"을 고르면 되므로 그냥 넘어갈 길은 막는다.
    if (teams.length > 0 && !signupTeamName) {
      setSignupMsg("팀을 선택해주세요.");
      return;
    }

    try {
      const res = await fetch(`${APP_API_BASE}/api/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: signupId,
          pw: signupPw,
          pwConfirm: signupPwConfirm,
          companyName: signupCompanyName,
          branchName: signupBranchName,
          // "해당 없음"은 화면에서만 쓰는 값이라 서버에는 빈 문자열로 보낸다.
          teamName: signupTeamName === TEAM_NONE ? "" : signupTeamName,
          vehicleNo: signupVehicleNo,
          name: signupName,
          phoneMid: "0000",
          phoneLast: signupPhoneLast,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setSignupMsg(data.message || "회원가입에 실패했습니다.");
        return;
      }

      setSignupMsg("회원가입이 완료되었습니다. 관리자 승인 후 로그인할 수 있습니다.");
      setAuthMode("login");
      setLoginId(signupId);

      setSignupId("");
      setSignupPw("");
      setSignupPwConfirm("");
      setSignupCompanyName("");
      setSignupName("");
      setSignupPhoneLast("");
      setSignupTeamName("");
      setSignupVehicleNo("");
      setSignupIdChecked(false);
    } catch {
      setSignupMsg("회원가입 중 오류가 발생했습니다.");
    }
  };

  const openDangerDismissModal = () => {
    if (!permissionFlags.canWatchDanger) return;
    if (!dangerCount || !activeDangerItem) return;
    setDangerModalOpen(true);
  };

  const closeDangerDismissModal = () => {
    if (dangerSubmitting) return;
    setDangerModalOpen(false);
    setWorkerName("");
    setDismissReason("");
  };

  // ── AI 안전장구(PPE) 미확인 위반 조회/확인 ──────────────────────
  const refreshActivePpe = async () => {
    try {
      if (!licenseFeatures.ppeEnabled || !permissionFlags.canEventData) {
        setActivePpe([]);
        setActivePpeTotal(0);
        return;
      }
      const res = await fetch(`${APP_API_BASE}/api/ppe-active`, {
        cache: "no-store",
        credentials: "include",
      });
      const data = await res.json().catch(() => null);
      const rows = Array.isArray(data?.items) ? data.items : [];
      setActivePpe(rows);
      // 카드 숫자는 서버가 센 total. 없으면(구버전 응답) 목록 길이로 대체한다.
      setActivePpeTotal(
        Number.isFinite(Number(data?.total)) ? Number(data.total) : rows.length
      );
    } catch {
      // 일시적 실패는 기존 목록 유지
    }
  };

  const openPpeModal = () => setPpeModalOpen(true);
  const closePpeModal = () => setPpeModalOpen(false);

  const handlePpeAck = async (id, ackBy, note) => {
    try {
      setPpeAckSubmittingId(id);
      const res = await fetch(`${APP_API_BASE}/api/ppe-ack`, {
        method: "POST",
        cache: "no-store",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, ackBy, note }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        alert(data?.message || "확인 처리에 실패했습니다.");
        return;
      }
      await refreshActivePpe();
    } catch {
      alert("확인 처리 중 오류가 발생했습니다.");
    } finally {
      setPpeAckSubmittingId(null);
    }
  };

  // '오탐이에요' — 텍스트 입력 없이 클릭 한 번으로 오탐 표시.
  // 서버가 오탐표시 + 위험에서 내림 + 재알림 중단 + 재학습 수집을 함께 처리한다.
  const handlePpeFalsePositive = async (id) => {
    try {
      setPpeAckSubmittingId(id);
      const res = await fetch(`${APP_API_BASE}/api/event/false-positive`, {
        method: "POST",
        cache: "no-store",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, type: "PPE_VIOLATION", value: true }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        alert(data?.message || "오탐 처리에 실패했습니다.");
        return;
      }
      await refreshActivePpe();
    } catch {
      alert("오탐 처리 중 오류가 발생했습니다.");
    } finally {
      setPpeAckSubmittingId(null);
    }
  };

  // 미확인 PPE 위반 주기적 조회(15초). PPE 라이선스 + 이벤트 권한일 때만.
  useEffect(() => {
    if (!licenseFeatures.ppeEnabled || !permissionFlags.canEventData) {
      setActivePpe([]);
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const run = async () => {
      await refreshActivePpe();
      if (!cancelled) timer = setTimeout(run, 5000);
    };
    run();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseFeatures.ppeEnabled, permissionFlags.canEventData]);

  const submitDangerDismiss = async () => {
    if (!permissionFlags.canWatchDanger) {
      alert("위험 해제 권한이 없습니다.");
      return;
    }

    if (!workerName.trim()) {
      alert("관제 근무자 이름을 입력해주세요.");
      return;
    }

    if (!dismissReason.trim()) {
      alert("해제 사유를 입력해주세요.");
      return;
    }

    try {
      setDangerSubmitting(true);

      const activeEventType = String(
        activeDangerItem?.eventType ||
          activeDangerItem?.type ||
          (String(activeDangerItem?.message || "").includes("심박수")
            ? "HEART_RATE_HIGH"
            : "SOS")
      )
        .trim()
        .toUpperCase();

      const normalizedEventType =
        activeEventType.includes("HEART") || activeEventType.includes("심박수")
          ? "HEART_RATE_HIGH"
          : "SOS";

      const body = {
        dismissedBy: workerName.trim(),
        dismissReason: dismissReason.trim(),
        dismissedReason: dismissReason.trim(),
        reason: dismissReason.trim(),
        deviceId:
          activeDangerItem?.deviceId ||
          activeDangerItem?.id ||
          activeDangerItem?.watchId ||
          "",
        deviceName:
          activeDangerItem?.deviceName ||
          activeDangerItem?.label ||
          activeDangerItem?.name ||
          "",
        siteId: activeDangerItem?.siteId || "",
        eventType: normalizedEventType,
        dangerType: normalizedEventType,
        type: normalizedEventType,
      };

      const res = await fetch(`${APP_API_BASE}/api/sos-dismiss`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.success === false || data?.ok === false) {
        alert(data?.message || data?.error || "위험 해제 처리에 실패했습니다.");
        return;
      }

      setDangerModalOpen(false);
      setWorkerName("");
      setDismissReason("");
      setDangerCount(0);
      setDangerText("현재 위험 상태를 표시합니다.");
      setActiveDangerItem(null);
      setAlertOpen(false);
      setToastItem(null);

      await pullDangerSummary();
    } catch {
      alert("위험 해제 처리 중 오류가 발생했습니다.");
    } finally {
      setDangerSubmitting(false);
    }
  };

  // 사이드바 접기(데스크톱 전용).
  //   접은 상태는 브라우저에 기억시켜 다음 접속에도 그대로 유지한다.
  //   모바일은 기존 서랍(mobileMenuOpen)이 따로 동작하므로 건드리지 않는다.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("monity.sidebar.collapsed.v1") === "1";
    } catch {
      return false;
    }
  });

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("monity.sidebar.collapsed.v1", next ? "1" : "0");
      } catch {}
      return next;
    });
  };

  const sidebarButtonClass = (active) =>
    `group flex w-full items-center rounded-2xl text-left transition-all duration-200 ${
      sidebarCollapsed ? "justify-center px-0 py-3 lg:px-0" : "gap-3 px-4 py-3"
    } ${
      active
        ? "bg-blue-600 text-white shadow-sm dark:bg-blue-500 dark:text-white"
        : "text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-white/10"
    }`;

  /*
    설정 그룹(설정 + 그 아래 관리자·작업 배정·공지)에 속한 화면인가.

    배경색과 아이콘 색이 반드시 같은 값을 써야 한다.
    예전에는 아이콘에 active 를 무조건 true 로 넘겨서, 설정 화면이 아닐 때
    흰 배경 위에 흰 아이콘이 되어 아이콘이 사라진 것처럼 보였다.

    admin-retention(데이터 보관 정책)은 원래 이 목록에서 빠져 있었다.
    그래서 그 화면에서는 하위 항목만 강조되고 상위 "설정" 버튼은 꺼져 보였다.
    같은 그룹이므로 함께 넣는다.
  */
  const settingsGroupActive =
    page === "settings" ||
    page === "admin-users" ||
    page === "admin-approval" ||
    page === "admin-cameras" ||
    page === "admin-retention" ||
    page === "admin-system" ||
    page === "owner-subscriptions" ||
    page === "work-schedule" ||
    page === "notice";

  const dashboardSummary = useMemo(() => {
    const totalCameras = Array.isArray(cameras) ? cameras.length : 0;

    // 오늘(KST) 날짜 문자열 "YYYY-MM-DD"
    const todayStr = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Seoul",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());

    const rowTime = (r) =>
      String(r?.timestamp || r?.createdAt || r?.displayTime || "");
    const isToday = (r) => rowTime(r).startsWith(todayStr);

    const canEvents = permissionFlags.canEventData && Array.isArray(logs);

    // "오늘 이벤트" = 알림성 이벤트만. 카메라 켜짐/꺼짐 같은 일상 운영기록은 제외
    // (이게 숫자 폭증의 주범이라 카운트에서 뺀다. 목록에는 그대로 남는다.)
    const ROUTINE_TYPES = new Set(["MINTCAM_ON", "MINTCAM_OFF"]);
    const todayEventTotal = canEvents
      ? logs.filter(
          (r) =>
            isToday(r) &&
            !ROUTINE_TYPES.has(String(r?.eventType || "").toUpperCase())
        ).length
      : 0;
    // 오늘 AI 안전장구(PPE) 미착용 건수
    const ppeToday = canEvents
      ? logs.filter(
          (r) =>
            String(r?.eventType || "").toUpperCase() === "PPE_VIOLATION" &&
            isToday(r)
        ).length
      : 0;

    return {
      totalCameras,
      activeCameras: onlineCount,
      // 카운트는 hook이 전체(비잘림)에서 센 값을 우선 사용(100칸 버퍼 잠식 버그 방지).
      // hook 값이 아직 없으면(초기) 기존 계산으로 폴백.
      // '오늘 이벤트'는 세 소스(①서버 집계 30s → ②hook 집계 8s → ③클라 폴백)의 최댓값을 사용.
      //   기존엔 서버값에 잠겨(lock) 빠른 소스를 무시 → 새 위반이 최대 30초 늦게 반영·비누적처럼 보임.
      //   셋 다 '오늘 이벤트 수'를 세므로 max면 가장 신선한 값이 즉시 오르고, 서버 폴링이 뒤따라 정합을 맞춘다.
      todayEvents: Math.max(
        typeof serverTodayCount === "number" ? serverTodayCount : 0,
        typeof alertTodayCount === "number" ? alertTodayCount : 0,
        typeof todayEventTotal === "number" ? todayEventTotal : 0
      ),
      ppeToday,
      recentLogs:
        permissionFlags.canEventData && Array.isArray(logs) ? logs.length : 0,
    };
  }, [cameras, logs, onlineCount, permissionFlags.canEventData, alertTodayCount, serverTodayCount]);

  // 미확인 PPE 위반 수 — 대시보드 '위험 감지' 카드에 사용(확인 처리해야 줄어듦).
  // 종(🔔)은 별개로, 이벤트 로그 기반 읽음/안읽음으로 동작한다(읽음 처리로 정리).
  const ppeActiveCount =
    licenseFeatures.ppeEnabled && permissionFlags.canEventData
      ? activePpeTotal
      : 0;

  const bellSignalCount = permissionFlags.canAlertCenter
    ? unreadAlerts +
      (permissionFlags.canWatchDanger && dangerCount > 0 ? dangerCount : 0)
    : 0;

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-gray-900 dark:bg-[#0A0F1F] dark:text-white">
        불러오는 중...
      </div>
    );
  }

  if (!me) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-[#0A0F1F]">
        <div className="w-full max-w-md overflow-hidden rounded-3xl bg-gradient-to-br from-[#10D0C0] via-[#16A4DF] to-[#1E78FF] p-7 shadow-2xl ring-1 ring-black/5 sm:p-9">
          <div className="mb-8 text-center">
            <img
              src="/assets/monity-icon-white.png"
              alt="Monity"
              className="mx-auto mb-3 h-40 w-40 object-contain drop-shadow-lg"
            />
            <h1 className="text-2xl font-extrabold tracking-tight text-white drop-shadow-sm">
              {authMode === "login" ? "MONITY 로그인" : "회원가입"}
            </h1>
            <p className="mt-2 text-sm text-white/85">
              {authMode === "login"
                ? "승인된 사용자만 플랫폼에 접속할 수 있습니다."
                : "가입 후 관리자 승인 시 이용 가능합니다."}
            </p>
          </div>

          {authMode === "login" ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="text"
                placeholder="아이디"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                className="w-full rounded-xl border border-white/50 bg-white px-4 py-3 text-slate-800 placeholder-slate-400 shadow-sm outline-none focus:ring-2 focus:ring-white/80"
              />
              <input
                type="password"
                placeholder="비밀번호"
                value={loginPw}
                onChange={(e) => setLoginPw(e.target.value)}
                className="w-full rounded-xl border border-white/50 bg-white px-4 py-3 text-slate-800 placeholder-slate-400 shadow-sm outline-none focus:ring-2 focus:ring-white/80"
              />
              {loginMsg && (
                <div className="rounded-lg bg-white/90 px-3 py-2 text-sm font-medium text-red-600 shadow-sm">
                  {loginMsg}
                </div>
              )}
              <button
                type="submit"
                disabled={loggingIn}
                className="w-full rounded-xl bg-[#0B3D80] py-3.5 font-bold text-white shadow-lg transition hover:bg-[#0a3670] active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loggingIn ? "로그인 중..." : "로그인"}
              </button>
              <div className="mt-6 text-center text-sm text-white/85">
                계정이 없으신가요?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode("signup");
                    setLoginMsg("");
                  }}
                  className="font-bold text-white underline underline-offset-2 hover:text-white/90"
                >
                  회원가입
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="아이디"
                  value={signupId}
                  onChange={(e) => setSignupId(e.target.value)}
                  className="flex-1 rounded-xl border border-white/50 bg-white px-4 py-3 text-slate-800 placeholder-slate-400 shadow-sm outline-none focus:ring-2 focus:ring-white/80"
                />
                <button
                  type="button"
                  onClick={handleCheckSignupId}
                  disabled={signupCheckingId}
                  className="shrink-0 rounded-xl border border-white/40 bg-white/20 px-4 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/30 disabled:opacity-60"
                >
                  {signupCheckingId ? "확인 중..." : "중복확인"}
                </button>
              </div>

              <input
                type="text"
                placeholder="회사명"
                value={signupCompanyName}
                onChange={(e) => setSignupCompanyName(e.target.value)}
                className="w-full rounded-xl border border-white/50 bg-white px-4 py-3 text-slate-800 placeholder-slate-400 shadow-sm outline-none focus:ring-2 focus:ring-white/80"
              />

              {branches.length > 0 && (
                <select
                  value={signupBranchName}
                  onChange={(e) => setSignupBranchName(e.target.value)}
                  className="w-full rounded-xl border border-white/50 bg-white px-4 py-3 text-slate-800 shadow-sm outline-none focus:ring-2 focus:ring-white/80"
                >
                  <option value="">지점 선택 (해당 회사 지점)</option>
                  {branches.map((b) => (
                    <option key={b.branchIdx} value={b.branchName}>
                      {b.branchName}
                    </option>
                  ))}
                </select>
              )}

              {/* 팀 — 그 지점에 등록된 팀이 있을 때만 뜬다(지점과 같은 방식) */}
              {teams.length > 0 && (
                <select
                  value={signupTeamName}
                  onChange={(e) => setSignupTeamName(e.target.value)}
                  className="w-full rounded-xl border border-white/50 bg-white px-4 py-3 text-slate-800 shadow-sm outline-none focus:ring-2 focus:ring-white/80"
                >
                  {/* 빈 값과 "팀 없음"을 반드시 구분한다.
                      앞서 기본 항목을 "팀 없음 / 관리자"로 뒀더니 작업자까지 그냥 지나쳐
                      TeamName 이 NULL 로 쌓였다(lee123). 빈 값은 '아직 안 고름'으로만 쓰고
                      제출을 막는다. 팀이 없는 관리자는 마지막 항목을 직접 고른다. */}
                  <option value="">팀을 선택하세요</option>
                  {teams.map((t) => (
                    <option key={t.teamIdx} value={t.teamName}>
                      {t.teamName}
                    </option>
                  ))}
                  <option value={TEAM_NONE}>해당 없음 (관리자·안전관리자)</option>
                </select>
              )}

              {/* 차량번호 — 이 칸을 쓰는 회사에서만 보여준다(회사별 설정).
                  비워도 가입된다. "(선택)" 꼬리표는 붙이지 않는다 — 직접 입력하는 칸이라
                  '선택'이 "고르세요"로 읽혀 혼동된다. */}
              {signupVehicleAsk && (
              <input
                type="text"
                placeholder="차량번호"
                value={signupVehicleNo}
                onChange={(e) => setSignupVehicleNo(e.target.value)}
                maxLength={30}
                className="w-full rounded-xl border border-white/50 bg-white px-4 py-3 text-slate-800 placeholder-slate-400 shadow-sm outline-none focus:ring-2 focus:ring-white/80"
              />
              )}

              <input
                type="text"
                placeholder="이름"
                value={signupName}
                onChange={(e) => setSignupName(e.target.value)}
                className="w-full rounded-xl border border-white/50 bg-white px-4 py-3 text-slate-800 placeholder-slate-400 shadow-sm outline-none focus:ring-2 focus:ring-white/80"
              />

              <div className="text-xs text-white/80">
                휴대폰 번호 끝 4자리를 입력해주세요.
              </div>

              <div className="grid grid-cols-[72px_16px_72px_16px_1fr] items-center gap-2">
                <div className="flex h-[52px] items-center justify-center rounded-xl border border-white/30 bg-white/15 text-base font-medium text-white">
                  010
                </div>

                <div className="text-center text-lg font-medium text-white/70">
                  -
                </div>

                <div className="flex h-[52px] items-center justify-center rounded-xl border border-white/30 bg-white/15 text-base font-medium text-white">
                  ****
                </div>

                <div className="text-center text-lg font-medium text-white/70">
                  -
                </div>

                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="끝 4자리 입력"
                  value={signupPhoneLast}
                  onChange={(e) =>
                    setSignupPhoneLast(
                      e.target.value.replace(/[^0-9]/g, "").slice(0, 4)
                    )
                  }
                  className="h-[52px] w-full rounded-xl border border-white/50 bg-white px-4 text-center text-slate-800 placeholder-slate-400 shadow-sm outline-none focus:ring-2 focus:ring-white/80"
                />
              </div>

              <input
                type="password"
                placeholder="비밀번호"
                value={signupPw}
                onChange={(e) => setSignupPw(e.target.value)}
                className="w-full rounded-xl border border-white/50 bg-white px-4 py-3 text-slate-800 placeholder-slate-400 shadow-sm outline-none focus:ring-2 focus:ring-white/80"
              />
              <input
                type="password"
                placeholder="비밀번호 확인"
                value={signupPwConfirm}
                onChange={(e) => setSignupPwConfirm(e.target.value)}
                className="w-full rounded-xl border border-white/50 bg-white px-4 py-3 text-slate-800 placeholder-slate-400 shadow-sm outline-none focus:ring-2 focus:ring-white/80"
              />

              {signupMsg && (
                <div
                  className={`rounded-lg bg-white/90 px-3 py-2 text-sm font-medium shadow-sm ${
                    signupMsg.includes("완료") || signupMsg.includes("사용 가능")
                      ? "text-emerald-600"
                      : "text-red-600"
                  }`}
                >
                  {signupMsg}
                </div>
              )}

              <button
                type="submit"
                className="w-full rounded-xl bg-[#0B3D80] py-3.5 font-bold text-white shadow-lg transition hover:bg-[#0a3670] active:scale-[0.99]"
              >
                회원가입
              </button>

              <div className="mt-6 text-center text-sm text-white/85">
                이미 계정이 있으신가요?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode("login");
                    setSignupMsg("");
                    setSignupIdChecked(false);
                  }}
                  className="font-bold text-white underline underline-offset-2 hover:text-white/90"
                >
                  로그인
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  // 관리자가 비밀번호를 초기화한 계정 — 바꾸기 전에는 어떤 화면도 열리지 않는다.
  if (me.mustChangePassword) {
    return (
      <ForcePasswordChange
        userName={me.name}
        apiBase={APP_API_BASE}
        onDone={refreshMe}
        onLogout={handleLogout}
      />
    );
  }

  if (me.approvalStatus !== 1 && !isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-[#0A0F1F]">
        <div className="w-full max-w-md rounded-3xl bg-white p-6 text-center shadow-xl dark:bg-[#1F2A3B] sm:p-8">
          <h1 className="mb-4 text-2xl font-bold">승인 대기중</h1>
          <p className="mb-6 text-gray-600 dark:text-gray-300">
            회원가입은 완료되었지만 아직 관리자 승인이 되지 않았습니다.
            <br />
            승인 후 다시 로그인해주세요.
          </p>
          <button
            onClick={handleLogout}
            className="rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white hover:bg-blue-700"
          >
            확인
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen overflow-x-hidden bg-[#EEF2F7] font-sans text-gray-900 dark:bg-[#0A0F1F] dark:text-white lg:h-screen lg:overflow-hidden">
        <div className="flex min-h-screen min-w-0 lg:h-full lg:min-h-0">
          {mobileMenuOpen && (
            <div
              className="fixed inset-0 z-40 bg-black/55 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
          )}

          <aside
            className={`fixed inset-y-0 left-0 z-50 flex w-[250px] max-w-[85vw] flex-col border-r border-gray-200 bg-white px-4 py-5 shadow-2xl transition-all duration-300 dark:border-white/10 dark:bg-[#0F172A] lg:static lg:z-auto lg:max-w-none lg:translate-x-0 lg:shadow-none ${
              sidebarCollapsed ? "lg:w-[76px] lg:px-2" : "lg:w-[250px]"
            } ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"}`}
          >
            <div className={`pb-5 ${sidebarCollapsed ? "px-0" : "px-2"}`}>
              {/* 접기 버튼은 사이드바 안에 둔다(여닫는 대상 옆이 자연스럽다).
                  펼친 상태: 로고 줄 오른쪽 끝 / 접힌 상태: 로고 아래 가운데 */}
              <div
                className={`flex gap-3 ${
                  sidebarCollapsed
                    ? "flex-col items-center"
                    : "items-center"
                }`}
              >
                <img
                  src="/assets/monity-logo.png"
                  alt="Monity Logo"
                  className="h-11 w-11 shrink-0 object-contain"
                />

                {!sidebarCollapsed && (
                  <div className="min-w-0 flex-1">
                    <div className="whitespace-nowrap bg-gradient-to-r from-teal-400 via-cyan-500 to-blue-600 bg-clip-text text-2xl font-extrabold tracking-tight text-transparent">
                      MONITY
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={toggleSidebar}
                  title={sidebarCollapsed ? "메뉴 펼치기" : "메뉴 접기"}
                  aria-label={sidebarCollapsed ? "메뉴 펼치기" : "메뉴 접기"}
                  aria-expanded={!sidebarCollapsed}
                  className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white lg:inline-flex"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            {/* 예전엔 여기에 "MONITY / 통합 관제 플랫폼 / 실시간 관제·영상 관리"
                안내 박스가 있었다. 맨 윗줄은 바로 위 워드마크와 중복이고
                나머지는 바뀌지 않는 고정 문구라, 세로 100px 가까이를 쓰면서
                화면에 알려주는 것이 없었다. 지우고 메뉴를 위로 올린다. */}

            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <nav className="flex flex-col gap-2">
                {visibleMenus.map((m) => {
                  const active = page === m.key;
                  return (
                    <button
                      key={m.key}
                      onClick={() => {
                        navigateToPage(m.key);

                        if (m.key !== "settings") {
                          setSettingsOpen(false);

                          if (
                            m.key !== "admin-users" &&
                            m.key !== "admin-approval" &&
                            m.key !== "admin-cameras" &&
                            m.key !== "admin-system"
                          ) {
                            setAdminOpen(false);
                          }
                        }
                      }}
                      className={sidebarButtonClass(active)}
                      title={sidebarCollapsed ? m.label : undefined}
                    >
                      <MenuIcon name={m.key} active={active} />
                      {/* 접히면 아이콘만. 이름은 마우스를 올리면 뜬다(title) */}
                      {!sidebarCollapsed && (
                        <>
                          <span className="truncate text-[15px] font-semibold">
                            {m.label}
                          </span>
                          {m.key === "safety-report" && (
                            <span className="ml-auto shrink-0 rounded-md bg-cyan-100 px-1.5 py-0.5 text-[10px] font-bold text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300">
                              AI
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}

                {hasPermission(me, "CanSettings") && (
                  <>
                    <button
                      onClick={() => {
                        // 접힌 상태에서는 하위 메뉴(글자만 있는 항목들)를 표시할
                        // 자리가 없다. 그래서 먼저 사이드바를 펴고 열어준다.
                        if (sidebarCollapsed) {
                          toggleSidebar();
                        }
                        setSettingsOpen((prev) => !prev);
                        navigateToPage("settings");
                      }}
                      className={sidebarButtonClass(settingsGroupActive)}
                    >
                      <MenuIcon name="settings" active={settingsGroupActive} />
                      {!sidebarCollapsed && (
                        <span className="flex-1 truncate text-[15px] font-semibold">
                          설정
                        </span>
                      )}
                    </button>

                    {settingsOpen && !sidebarCollapsed && (
                      <div className="ml-4 flex flex-col gap-1">
                        <button
                          onClick={() => navigateToPage("settings")}
                          className={`rounded-xl px-3 py-2 text-left text-sm transition ${
                            page === "settings"
                              ? "bg-blue-50 text-blue-700 dark:bg-[#1E293B] dark:text-blue-300"
                              : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                          }`}
                        >
                          일반 설정
                        </button>

                        {isAdmin && (
                          <>
                            <button
                              onClick={() => setAdminOpen((prev) => !prev)}
                              className={`flex items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition ${
                                page === "admin-users" ||
                                page === "admin-approval" ||
                                page === "admin-cameras" ||
                                page === "admin-retention" ||
                                page === "admin-system" ||
                                page === "owner-subscriptions" ||
                                page === "work-schedule" ||
                                page === "notice"
                                  ? "bg-blue-50 text-blue-700 dark:bg-[#1E293B] dark:text-blue-300"
                                  : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                              }`}
                            >
                              <span>관리자</span>
                              <span>{adminOpen ? "▾" : "▸"}</span>
                            </button>

                            {adminOpen && (
                              <div className="ml-3 flex flex-col gap-1">
                                <button
                                  onClick={() => navigateToPage("admin-users")}
                                  className={`rounded-xl px-3 py-2 text-left text-sm transition ${
                                    page === "admin-users"
                                      ? "bg-blue-50 text-blue-700 dark:bg-[#1E293B] dark:text-blue-300"
                                      : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                                  }`}
                                >
                                  사용자 관리
                                </button>
                                <button
                                  onClick={() => navigateToPage("admin-approval")}
                                  className={`rounded-xl px-3 py-2 text-left text-sm transition ${
                                    page === "admin-approval"
                                      ? "bg-blue-50 text-blue-700 dark:bg-[#1E293B] dark:text-blue-300"
                                      : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                                  }`}
                                >
                                  승인 관리
                                </button>
                                <button
                                  onClick={() => navigateToPage("admin-cameras")}
                                  className={`rounded-xl px-3 py-2 text-left text-sm transition ${
                                    page === "admin-cameras"
                                      ? "bg-blue-50 text-blue-700 dark:bg-[#1E293B] dark:text-blue-300"
                                      : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                                  }`}
                                >
                                  카메라 관리
                                </button>
                                {/* 데이터 보관 정책 — 최고관리자·본사 안전관리자만 */}
                                {["SUPER_ADMIN", "HQ_MANAGER"].includes(
                                  String((me && (me.roleCode || me.role)) || "").toUpperCase()
                                ) && (
                                  <button
                                    onClick={() => navigateToPage("admin-retention")}
                                    className={`rounded-xl px-3 py-2 text-left text-sm transition ${
                                      page === "admin-retention"
                                        ? "bg-blue-50 text-blue-700 dark:bg-[#1E293B] dark:text-blue-300"
                                        : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                                    }`}
                                  >
                                    데이터 보관 정책
                                  </button>
                                )}
                                {isSuperAdmin && (
                                  <button
                                    onClick={() => navigateToPage("admin-system")}
                                    className={`rounded-xl px-3 py-2 text-left text-sm transition ${
                                      page === "admin-system"
                                        ? "bg-blue-50 text-blue-700 dark:bg-[#1E293B] dark:text-blue-300"
                                        : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                                    }`}
                                  >
                                    시스템 설정
                                  </button>
                                )}
                                {/* 작업 배정 — 삼우용으로 만든 기능. 회사 구독에서 끄면 숨는다. */}
                                {companyFeatures?.workScheduleEnabled !== false &&
                                  ["SUPER_ADMIN", "ADMIN", "HQ_MANAGER", "BRANCH_MANAGER"].includes(
                                  String(me?.roleCode || "").toUpperCase()
                                ) && (
                                  <button
                                    onClick={() => navigateToPage("work-schedule")}
                                    className={`rounded-xl px-3 py-2 text-left text-sm transition ${
                                      page === "work-schedule"
                                        ? "bg-blue-50 text-blue-700 dark:bg-[#1E293B] dark:text-blue-300"
                                        : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                                    }`}
                                  >
                                    작업 배정
                                  </button>
                                )}
                                {/* 공지 발송 — 삼우용으로 만든 기능. 회사 구독에서 끄면 숨는다. */}
                                {companyFeatures?.noticeEnabled !== false &&
                                  ["SUPER_ADMIN", "ADMIN", "HQ_MANAGER", "BRANCH_MANAGER"].includes(
                                  String(me?.roleCode || "").toUpperCase()
                                ) && (
                                  <button
                                    onClick={() => navigateToPage("notice")}
                                    className={`rounded-xl px-3 py-2 text-left text-sm transition ${
                                      page === "notice"
                                        ? "bg-blue-50 text-blue-700 dark:bg-[#1E293B] dark:text-blue-300"
                                        : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                                    }`}
                                  >
                                    공지 발송
                                  </button>
                                )}
                              </div>
                            )}
                          </>
                        )}

                        {/*
                          운영사 전용 — 우리가 «고객사를 관리하는» 메뉴.
                          고객사 관리 화면(관리자 그룹)과 섞이면 안 되므로 구분선을 두고 따로 둔다.
                          납품본에서는 REACT_APP_OWNER_CONSOLE=false 로 빌드하면 통째로 사라진다.
                        */}
                        {canSeeOwnerConsole(me) && (
                          <>
                            <div className="my-1 border-t border-gray-200 dark:border-white/10" />
                            <div className="px-3 pb-1 pt-1 text-[11px] font-semibold tracking-wide text-gray-400">
                              운영사
                            </div>
                            <button
                              onClick={() => navigateToPage("owner-subscriptions")}
                              className={`rounded-xl px-3 py-2 text-left text-sm transition ${
                                page === "owner-subscriptions"
                                  ? "bg-blue-50 text-blue-700 dark:bg-[#1E293B] dark:text-blue-300"
                                  : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                              }`}
                            >
                              구독 관리
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </nav>
            </div>

            {!sidebarCollapsed && (
              <div className="pt-5 text-center text-xs text-gray-400 dark:text-gray-500">
                © 2026 MONITY
              </div>
            )}
          </aside>

          <main className="relative z-0 flex-1 min-h-screen min-w-0 overflow-x-hidden p-3 pb-[calc(96px+env(safe-area-inset-bottom))] sm:p-5 sm:pb-[calc(96px+env(safe-area-inset-bottom))] lg:h-full lg:min-h-0 lg:overflow-hidden lg:p-6 lg:pb-6">
            <div className="mx-auto flex min-h-full max-w-[1600px] flex-col lg:h-full lg:min-h-0">
              <div className="mb-3 flex shrink-0 flex-col gap-3 lg:mb-4 lg:flex-row lg:items-start lg:justify-between lg:gap-4">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gray-400 dark:text-gray-500">
                    MONITY PLATFORM
                  </div>
                  <h1 className="mt-1 max-w-full overflow-hidden text-2xl font-bold tracking-tight text-gray-900 text-ellipsis whitespace-nowrap break-keep dark:text-white sm:text-[28px]">
                    {getTitle()}
                  </h1>
                </div>

                <div className="relative z-10 flex w-full flex-wrap items-center gap-2 rounded-3xl border border-gray-200 bg-white px-3 py-2 shadow-sm dark:border-white/10 dark:bg-[#111827] lg:w-auto lg:flex-nowrap lg:justify-end">
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(true)}
                    className="inline-flex shrink-0 items-center justify-center rounded-2xl bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-800 dark:bg-white/10 dark:text-white lg:hidden"
                  >
                    ☰ 메뉴
                  </button>

                  {permissionFlags.canAlertCenter && (
                    <>
                      <div className="shrink-0">
                        <NotificationBell
                          count={bellSignalCount}
                          hasSignal={unreadAlerts > 0 || dangerCount > 0}
                          onClick={() => setAlertOpen((prev) => !prev)}
                        />
                      </div>

                      <div className="hidden h-7 w-px bg-gray-200 dark:bg-white/10 sm:block" />
                    </>
                  )}

                  {/* 라이선스 배지: 고객 관제 대시보드에서 숨김.
                      라이선스(maxDevices 제한·복제방지)는 백그라운드에서 그대로 작동하며,
                      본사는 server-manager 앱의 라이선스 탭에서 상태를 확인한다. */}

                  <button
                    onClick={() => setDark(!dark)}
                    className="shrink-0 rounded-2xl bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 dark:bg-white/10 dark:text-gray-100 dark:hover:bg-white/15"
                  >
                    {dark ? "☀ 라이트" : "🌙 다크 모드"}
                  </button>

                  <div className="hidden h-7 w-px bg-gray-200 dark:bg-white/10 lg:block" />

                  <div
                    onClick={handleNameClick}
                    title=""
                    className="order-last w-full select-none rounded-2xl bg-gray-50 px-3 py-2 text-sm font-medium text-gray-500 dark:bg-white/5 dark:text-gray-300 sm:order-none sm:w-auto sm:max-w-[260px] sm:truncate sm:bg-transparent sm:px-1 sm:py-0">
                    {me
                      ? me.roleCode === "HQ_MANAGER"
                        ? `본사 안전관리자 _ ${me.name || me.id} 님`
                        : me.roleCode === "BRANCH_MANAGER"
                        ? `${me.branchName ? me.branchName + " " : ""}안전관리자 _ ${me.name || me.id} 님`
                        : `${me.name || me.id} 님`
                      : "사용자"}
                  </div>

                  <button
                    onClick={handleLogout}
                    className="shrink-0 rounded-2xl px-3 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-500/10"
                  >
                    로그아웃
                  </button>
                </div>
              </div>

              <div
                className={`min-h-0 flex-1 pb-4 lg:pb-0 ${
                  page === "playback"
                    ? "lg:overflow-hidden"
                    : "lg:overflow-y-auto lg:overflow-x-hidden"
                }`}
              >
                {page === "dashboard" && (
                  <DashboardPage
                    me={me}
                    permissionFlags={permissionFlags}
                    cameras={cameras}
                    camerasLoading={camerasLoading}
                    dangerText={dangerText}
                    dangerCount={dangerCount}
                    logs={permissionFlags.canEventData ? logs : []}
                    onNavigate={navigateToPage}
                    summary={dashboardSummary}
                    licenseFeatures={licenseFeatures}
                    activePpeCount={ppeActiveCount}
                    onPpeDangerClick={openPpeModal}
                    onDangerClick={openDangerDismissModal}
                  />
                )}

                {page === "live" && (
                  <LiveView
                    cameras={cameras}
                    loading={camerasLoading}
                    onRefresh={refreshMyCameras}
                  />
                )}

                {page === "playback" &&
                  (playbackAllowed ? (
                    <PlaybackPage />
                  ) : (
                    <LicenseRestrictedPanel
                      licenseStatus={licenseStatus}
                      onNavigate={navigateToPage}
                    />
                  ))}

                {page === "events" && (
                  <EventsPage
                    me={me}
                    permissionFlags={permissionFlags}
                    cameras={cameras}
                    licenseFeatures={licenseFeatures}
                  />
                )}

                {page === "safety-report" && <SafetyReportPage me={me} />}

                {page === "work-schedule" &&
                  ["SUPER_ADMIN", "ADMIN", "HQ_MANAGER", "BRANCH_MANAGER"].includes(
                    String(me?.roleCode || "").toUpperCase()
                  ) && <WorkSchedulePage me={me} />}

                {page === "notice" &&
                  ["SUPER_ADMIN", "ADMIN", "HQ_MANAGER", "BRANCH_MANAGER"].includes(
                    String(me?.roleCode || "").toUpperCase()
                  ) && <NoticeSenderPage me={me} />}

                {page === "messages" && <MessagesPage />}

                {page === "devices" && (
                  <DevicesPage me={me} permissionFlags={permissionFlags} />
                )}

                {page === "workers" && <ComingSoon title="작업자" />}
                {page === "map" && <MapPage />}
                {page === "logs" && <ComingSoon title="로그" />}

                {page === "settings" && (
                  <SettingsPage me={me} permissionFlags={permissionFlags} />
                )}
                {page === "admin-users" && <AdminUsersPage />}
                {page === "admin-approval" && <AdminApprovalPage me={me} />}
                {page === "admin-cameras" && <AdminCamerasPage me={me} />}
                {page === "admin-retention" && <AdminRetentionPage />}
                {OWNER_CONSOLE_ENABLED && page === "owner-subscriptions" && (
                  <React.Suspense
                    fallback={
                      <div className="p-8 text-center text-sm text-gray-500">
                        불러오는 중…
                      </div>
                    }
                  >
                    <OwnerSubscriptionPage />
                  </React.Suspense>
                )}
                {page === "admin-system" && <AdminSystemPage me={me} />}
              </div>
            </div>
          </main>
        </div>
      </div>

      {permissionFlags.canAlertCenter && (
        <AlertPanel
          open={alertOpen}
          logs={recentAlertLogs}
          onClose={() => setAlertOpen(false)}
          onClear={handleClearAlerts}
        />
      )}

      {permissionFlags.canWatchDanger && (
        <DangerDismissModal
          open={dangerModalOpen}
          onClose={closeDangerDismissModal}
          onSubmit={submitDangerDismiss}
          workerName={workerName}
          setWorkerName={setWorkerName}
          dismissReason={dismissReason}
          setDismissReason={setDismissReason}
          submitting={dangerSubmitting}
          dangerText={dangerText}
        />
      )}

      <PpeAckModal
        open={ppeModalOpen}
        onClose={closePpeModal}
        items={activePpe}
        total={activePpeTotal}
        onAck={handlePpeAck}
        onFalsePositive={handlePpeFalsePositive}
        submittingId={ppeAckSubmittingId}
        ackByName={
          me
            ? me.roleCode === "HQ_MANAGER"
              ? `본사 안전관리자 _ ${me.name || me.id} 님`
              : me.roleCode === "BRANCH_MANAGER"
              ? `${me.branchName ? me.branchName + " " : ""}안전관리자 _ ${me.name || me.id} 님`
              : `${me.name || me.id} 님`
            : ""
        }
      />

      {/* 서버 점검 패널 (히든 메뉴) */}
      <SystemHealthPanel open={healthOpen} onClose={() => setHealthOpen(false)} />

      {permissionFlags.canAlertCenter && toastItem && (
        <div className="pointer-events-none fixed right-4 top-20 z-[100001]">
          <AlertToast item={toastItem} onClose={() => setToastItem(null)} />
        </div>
      )}
    </>
  );
}